import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ShareModal from '../../components/ShareModal';
import { getReferralCode, getReferralUrl, getSiteUrl } from '../../utils/referral';
import { referralService } from '../../services';

// ── Tier config ────────────────────────────────────────────────
const TIERS = {
  silver:   { label: 'Silver',   icon: '🥈', min: 0,    max: 999,  color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', next: 'Gold',     nextMin: 1000 },
  gold:     { label: 'Gold',     icon: '🥇', min: 1000, max: 2499, color: '#d97706', bg: '#fffbeb', border: '#fde68a', next: 'Platinum', nextMin: 2500 },
  platinum: { label: 'Platinum', icon: '💎', min: 2500, max: null, color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', next: null,       nextMin: null },
};

const TIER_BENEFITS = [
  { feature: 'Session discount',         silver: '5%',         gold: '10%',             platinum: '15%'              },
  { feature: 'Support priority',         silver: 'Standard',   gold: 'Priority',         platinum: 'VIP (24h)'        },
  { feature: 'Referral bonus',           silver: '₹500/ref',   gold: '₹600/ref',         platinum: '₹750/ref'         },
  { feature: 'Profile badge',            silver: '🥈 Silver',  gold: '🥇 Gold',          platinum: '💎 Platinum'      },
  { feature: 'Early access to features', silver: '✗',          gold: '✓',               platinum: '✓'                },
  { feature: 'Free intro sessions',      silver: '1/month',    gold: '2/month',          platinum: 'Unlimited'        },
];

const POINT_EVENTS = [
  { type: 'referral_signup',    icon: '👤', label: 'Friend signs up',         pts: 100,  color: '#3b82f6' },
  { type: 'referral_converted', icon: '📅', label: 'Friend books a session',  pts: 250,  color: '#1DB954' },
  { type: 'event_booked',       icon: '🎓', label: 'Book an event',            pts: 50,   color: '#8b5cf6' },
  { type: 'profile_complete',   icon: '✅', label: 'Complete your profile',    pts: 100,  color: '#f59e0b' },
  { type: 'first_booking',      icon: '🚀', label: 'Book your first session',  pts: 150,  color: '#ef4444' },
  { type: 'review_left',        icon: '⭐', label: 'Leave a review',           pts: 25,   color: '#d97706' },
];

function getTier(points) {
  if (points >= 2500) return 'platinum';
  if (points >= 1000) return 'gold';
  return 'silver';
}

function CopyBox({ value, hint }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={value}
          style={{ flex: 1, padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 12, color: '#374151', background: '#f9fafb', outline: 'none' }}
          onClick={e => e.target.select()} />
        <button onClick={() => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
          style={{ padding: '10px 16px', background: copied ? '#1DB954' : '#1a1a2e', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', transition: 'background .2s' }}>
          {copied ? '✅ Copied!' : '📋 Copy'}
        </button>
      </div>
      {hint && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

const statusStyle = {
  credited:  { bg: '#f0fdf4', color: '#16a34a', label: '✅ Credit Earned' },
  booked:    { bg: '#f0fdf4', color: '#16a34a', label: '✅ Credit Earned' },
  signed_up: { bg: '#eff6ff', color: '#2563eb', label: '⏳ Signed up' },
  pending:   { bg: '#fefce8', color: '#d97706', label: '🔗 Link clicked' },
};

const activityIcon = {
  referral_converted: { icon: '📅', color: '#1DB954' },
  referral_signup:    { icon: '👤', color: '#3b82f6' },
  event_booked:       { icon: '🎓', color: '#8b5cf6' },
  profile_complete:   { icon: '✅', color: '#f59e0b' },
  first_booking:      { icon: '🚀', color: '#ef4444' },
  review_left:        { icon: '⭐', color: '#d97706' },
};

export default function MenteeReferPage() {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);

  const [referrals, setReferrals] = useState([]);
  const [totalEarned, setTotalEarned] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const [pointsData, setPointsData] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(true);

  const [activeTab, setActiveTab] = useState('overview'); // overview | history | tiers

  const refCode     = getReferralCode(user?.id);
  const referralUrl = getReferralUrl(refCode);
  const siteBase    = getSiteUrl();

  useEffect(() => {
    referralService.getMyReferrals().then(res => {
      if (res.referrals) setReferrals(res.referrals);
      setTotalEarned(res.totalEarned || 0);
      setPendingCount(res.pending || 0);
    }).catch(() => {});

    setPointsLoading(true);
    referralService.getMyPoints().then(res => {
      setPointsData(res);
    }).catch(() => {}).finally(() => setPointsLoading(false));
  }, []);

  const totalPoints  = pointsData?.totalPoints  ?? 0;
  const tierKey      = pointsData?.tier         ?? getTier(totalPoints);
  const tier         = TIERS[tierKey];
  const nextTierPts  = tier.nextMin;
  const progress     = nextTierPts ? Math.min((totalPoints - tier.min) / (nextTierPts - tier.min) * 100, 100) : 100;
  const ptsToNext    = nextTierPts ? Math.max(nextTierPts - totalPoints, 0) : 0;
  const breakdown    = pointsData?.breakdown    ?? {};
  const activity     = pointsData?.recentActivity ?? [];

  return (
    <div style={{ maxWidth: 760, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <h1 className="db-page-title">Refer &amp; Earn</h1>
      <p className="db-page-sub">Invite friends, earn points, unlock Silver · Gold · Platinum rewards.</p>

      {/* ── Environment indicator (dev/QA only) ─────────────── */}
      {!siteBase.includes('devbrains.in') && (
        <div style={{ marginBottom: 14, padding: '8px 14px', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 9, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
          🔧 Dev mode — referral links point to <code style={{ background: '#fef08a', padding: '1px 5px', borderRadius: 4 }}>{siteBase}</code>
        </div>
      )}

      {/* ── Tier Hero Card ───────────────────────────────────── */}
      <div style={{ background: `linear-gradient(135deg, #1a1a2e, #16213e)`, borderRadius: 20, padding: '24px 28px', marginBottom: 20, color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: tier.color + '20' }} />
        <div style={{ position: 'absolute', bottom: -20, right: 60, width: 80, height: 80, borderRadius: '50%', background: tier.color + '15' }} />

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap', position: 'relative' }}>
          {/* Badge */}
          <div style={{ width: 72, height: 72, borderRadius: '50%', background: tier.bg, border: `3px solid ${tier.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.2rem', flexShrink: 0 }}>
            {tier.icon}
          </div>

          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.4rem', fontWeight: 900 }}>{tier.icon} {tier.label} Member</span>
              <span style={{ padding: '3px 10px', borderRadius: 100, background: tier.color + '30', color: tier.color, fontSize: '.68rem', fontWeight: 800, border: `1px solid ${tier.color}50` }}>
                {tier.label.toUpperCase()}
              </span>
            </div>
            <div style={{ fontSize: '.82rem', color: 'rgba(255,255,255,.65)', marginBottom: 14 }}>
              {pointsLoading ? '—' : `${totalPoints.toLocaleString()} points earned`}
            </div>

            {/* Progress bar */}
            {tier.next && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.68rem', color: 'rgba(255,255,255,.5)', marginBottom: 5 }}>
                  <span>{tier.label} ({tier.min.toLocaleString()} pts)</span>
                  <span>{tier.next} ({tier.nextMin.toLocaleString()} pts)</span>
                </div>
                <div style={{ height: 7, background: 'rgba(255,255,255,.15)', borderRadius: 100, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: tier.color, borderRadius: 100, transition: 'width 1s ease' }} />
                </div>
                <div style={{ fontSize: '.7rem', color: 'rgba(255,255,255,.55)', marginTop: 5 }}>
                  {pointsLoading ? '…' : `${ptsToNext.toLocaleString()} more points to ${tier.next}`}
                </div>
              </div>
            )}
            {!tier.next && (
              <div style={{ padding: '6px 14px', background: tier.color + '30', borderRadius: 100, display: 'inline-block', fontSize: '.74rem', fontWeight: 700, color: tier.color, border: `1px solid ${tier.color}50` }}>
                💎 Maximum tier reached!
              </div>
            )}
          </div>

          {/* Points total */}
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '2.4rem', fontWeight: 900, color: tier.color, lineHeight: 1 }}>
              {pointsLoading ? '—' : totalPoints.toLocaleString()}
            </div>
            <div style={{ fontSize: '.65rem', color: 'rgba(255,255,255,.45)', textTransform: 'uppercase', letterSpacing: '.08em', marginTop: 2 }}>Total Points</div>
          </div>
        </div>
      </div>

      {/* ── Stats row ────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { icon: '💰', label: 'Credits Earned', value: `₹${totalEarned.toLocaleString()}`, color: '#1DB954'  },
          { icon: '👥', label: 'Total Referrals', value: referrals.length,                  color: '#3b82f6'  },
          { icon: '⏳', label: 'Pending',          value: pendingCount,                      color: '#f59e0b'  },
          { icon: '🏅', label: 'Points',           value: pointsLoading ? '…' : totalPoints.toLocaleString(), color: tier.color },
        ].map(({ icon, label, value, color }) => (
          <div key={label} style={{ flex: 1, minWidth: 120, background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12, padding: '14px 18px' }}>
            <div style={{ fontSize: 20 }}>{icon}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Referral link ────────────────────────────────────── */}
      <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#1a1a2e', fontWeight: 800 }}>🔗 Your Referral Link</h3>
          <button onClick={() => setShowModal(true)}
            style={{ padding: '8px 18px', background: '#1DB954', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
            🚀 Share Now
          </button>
        </div>
        <CopyBox value={referralUrl} hint={`Code: ${refCode} · Points to: ${siteBase}`} />

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {[
            { icon: '💬', label: 'WhatsApp', color: '#25D366', url: `https://wa.me/?text=${encodeURIComponent(`🎁 Join me on DevBrains and we both get ₹500 credit!\n\n🧠 Learn from top tech mentors 1:1.\n\nUse my link: ${referralUrl}`)}` },
            { icon: '🐦', label: 'Twitter',  color: '#1DA1F2', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Learning from top mentors on @DevBrains_in 🧠\n\nJoin with my referral link — we both get ₹500 credit 🎁\n${referralUrl}`)}` },
            { icon: '💼', label: 'LinkedIn', color: '#0A66C2', url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(referralUrl)}` },
            { icon: '✈️', label: 'Telegram', color: '#26A5E4', url: `https://t.me/share/url?url=${encodeURIComponent(referralUrl)}&text=${encodeURIComponent('Join DevBrains and we both get ₹500 credit 🎁')}` },
          ].map(({ icon, label, color, url }) => (
            <button key={label} onClick={() => window.open(url, '_blank')} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '7px 13px',
              background: color + '15', border: `1.5px solid ${color}30`, borderRadius: 8,
              cursor: 'pointer', fontSize: 12, fontWeight: 600, color,
            }}>
              <span>{icon}</span><span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, borderBottom: '1px solid #e5e7eb', paddingBottom: 0 }}>
        {[
          { key: 'overview', label: '📊 Points & Activity' },
          { key: 'history',  label: '👥 Referral History'  },
          { key: 'tiers',    label: '🏆 Tier Benefits'     },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding: '9px 16px', border: 'none', borderBottom: activeTab === t.key ? '2.5px solid #1DB954' : '2.5px solid transparent',
              background: 'none', cursor: 'pointer', fontWeight: activeTab === t.key ? 800 : 600,
              fontSize: 13, color: activeTab === t.key ? '#1DB954' : '#6b7280', transition: 'all .15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Points & Activity ───────────────────────────── */}
      {activeTab === 'overview' && (
        <div>
          {/* How to earn */}
          <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#1a1a2e', fontWeight: 800 }}>🎯 How to Earn Points</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 10 }}>
              {POINT_EVENTS.map(e => (
                <div key={e.type} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', background: '#f9fafb', borderRadius: 10, border: '1px solid #f3f4f6' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: e.color + '18', border: `1.5px solid ${e.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', flexShrink: 0 }}>
                    {e.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{e.label}</div>
                    <div style={{ fontSize: 13, fontWeight: 900, color: e.color, marginTop: 1 }}>+{e.pts} pts</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Points breakdown */}
          {pointsData && (
            <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#1a1a2e', fontWeight: 800 }}>📈 Your Points Breakdown</h3>
              {[
                { label: 'Referrals',         value: breakdown.referrals       || 0, color: '#1DB954',  icon: '👥' },
                { label: 'Events Booked',     value: breakdown.eventsBooked    || 0, color: '#8b5cf6',  icon: '🎓' },
                { label: 'Profile Complete',  value: breakdown.profileComplete || 0, color: '#f59e0b',  icon: '✅' },
                { label: 'First Booking',     value: breakdown.firstBooking    || 0, color: '#ef4444',  icon: '🚀' },
                { label: 'Reviews Left',      value: breakdown.reviewsLeft     || 0, color: '#d97706',  icon: '⭐' },
                { label: 'Other',             value: breakdown.other           || 0, color: '#6b7280',  icon: '🎁' },
              ].filter(b => b.value > 0).map(b => {
                const pct = totalPoints > 0 ? (b.value / totalPoints) * 100 : 0;
                return (
                  <div key={b.label} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{b.icon} {b.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: b.color }}>{b.value.toLocaleString()} pts</span>
                    </div>
                    <div style={{ height: 5, background: '#f3f4f6', borderRadius: 100, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: b.color, borderRadius: 100, transition: 'width .8s ease' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Recent activity */}
          <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px 24px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#1a1a2e', fontWeight: 800 }}>⚡ Recent Activity</h3>
            {pointsLoading ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13 }}>Loading…</div>
            ) : activity.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: '#9ca3af', fontSize: 13 }}>No activity yet. Start earning points!</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {activity.map((a, i) => {
                  const cfg = activityIcon[a.type] || { icon: '🎁', color: '#6b7280' };
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < activity.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                      <div style={{ width: 34, height: 34, borderRadius: '50%', background: cfg.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '.9rem', flexShrink: 0 }}>
                        {cfg.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{a.desc}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{a.date}</div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 900, color: cfg.color, flexShrink: 0 }}>+{a.points}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab: Referral History ────────────────────────────── */}
      {activeTab === 'history' && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px 24px' }}>
          {referrals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔗</div>
              <div style={{ fontWeight: 600 }}>No referrals yet</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Share your link to start earning!</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Friend', 'Date', 'Status', 'Credit', 'Points'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((r, i) => {
                    const s = statusStyle[r.status] || statusStyle.pending;
                    const pts = r.status === 'credited' || r.status === 'booked' ? 250 : r.status === 'signed_up' ? 100 : 0;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px', color: '#1a1a2e', fontWeight: 600 }}>
                          {r.name}<br /><span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 400 }}>{r.email}</span>
                        </td>
                        <td style={{ padding: '12px', color: '#6b7280' }}>{r.date}</td>
                        <td style={{ padding: '12px' }}>
                          <span style={{ background: s.bg, color: s.color, padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{s.label}</span>
                        </td>
                        <td style={{ padding: '12px', fontWeight: 700, color: r.credit ? '#1DB954' : '#9ca3af' }}>
                          {r.credit ? `+₹${r.credit}` : '—'}
                        </td>
                        <td style={{ padding: '12px', fontWeight: 700, color: pts ? '#7c3aed' : '#9ca3af' }}>
                          {pts ? `+${pts} pts` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '11px 14px', fontSize: 12, color: '#166534' }}>
            💡 Credits expire after <strong>6 months</strong>. No limit on referrals — refer 10 friends, earn ₹5,000!
          </div>
        </div>
      )}

      {/* ── Tab: Tier Benefits ───────────────────────────────── */}
      {activeTab === 'tiers' && (
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0, overflowX: 'auto' }}>
            {/* Header row */}
            <div style={{ padding: '10px 12px', fontWeight: 700, fontSize: 12, color: '#9ca3af', borderBottom: '2px solid #f3f4f6' }}>Feature</div>
            {Object.values(TIERS).map(t => (
              <div key={t.label} style={{
                padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #f3f4f6',
                background: tierKey === t.label.toLowerCase() ? t.bg : 'transparent',
                borderRadius: tierKey === t.label.toLowerCase() ? '8px 8px 0 0' : 0,
              }}>
                <div style={{ fontSize: '1.4rem' }}>{t.icon}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: t.color }}>{t.label}</div>
                <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                  {t.max ? `${t.min.toLocaleString()}–${t.max.toLocaleString()} pts` : `${t.min.toLocaleString()}+ pts`}
                </div>
                {tierKey === t.label.toLowerCase() && (
                  <div style={{ marginTop: 4, padding: '2px 8px', background: t.color + '20', borderRadius: 100, fontSize: 10, fontWeight: 700, color: t.color, display: 'inline-block' }}>
                    Your tier
                  </div>
                )}
              </div>
            ))}

            {/* Benefit rows */}
            {TIER_BENEFITS.map((row, i) => (
              <React.Fragment key={row.feature}>
                <div style={{ padding: '12px', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '1px solid #f9fafb', background: i % 2 === 0 ? '#fafafa' : '#fff' }}>
                  {row.feature}
                </div>
                {[row.silver, row.gold, row.platinum].map((val, j) => {
                  const tk = ['silver','gold','platinum'][j];
                  const isActive = tierKey === tk;
                  return (
                    <div key={j} style={{ padding: '12px', textAlign: 'center', fontSize: 12, fontWeight: isActive ? 700 : 500,
                      color: isActive ? TIERS[tk].color : val === '✗' ? '#d1d5db' : '#374151',
                      borderBottom: '1px solid #f9fafb', background: isActive ? TIERS[tk].bg + '80' : i % 2 === 0 ? '#fafafa' : '#fff' }}>
                      {val}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>

          {/* How to level up */}
          <div style={{ marginTop: 20, background: 'linear-gradient(135deg,#1a1a2e,#16213e)', borderRadius: 12, padding: '16px 20px', color: '#fff' }}>
            <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 10 }}>🚀 Level Up Faster</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { label: `${ptsToNext > 0 ? ptsToNext.toLocaleString() + ' pts' : '✓'} to ${tier.next || 'Max tier'}`, color: tier.color },
                { label: `Refer 2 friends → +500 pts`,   color: '#1DB954' },
                { label: `Book 2 events → +100 pts`,      color: '#8b5cf6' },
                { label: `Complete profile → +100 pts`,   color: '#f59e0b' },
              ].map(b => (
                <span key={b.label} style={{ padding: '5px 12px', borderRadius: 100, background: b.color + '25', color: b.color, fontSize: 11, fontWeight: 700, border: `1px solid ${b.color}40` }}>
                  {b.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <ShareModal
          mentor={null}
          shareUrl={referralUrl}
          referralCode={refCode}
          onClose={() => setShowModal(false)}
          mode="mentee"
        />
      )}
    </div>
  );
}
